import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, toDisplayString as _toDisplayString, Fragment as _Fragment, renderList as _renderList, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["title", "aria-label", "aria-expanded"]
const _hoisted_2 = {
  key: 0,
  class: "ag-label"
}
const _hoisted_3 = { class: "ag-pop-head" }
const _hoisted_4 = { class: "ag-name" }
const _hoisted_5 = { class: "ag-facts" }
const _hoisted_6 = { class: "ag-actions" }
const _hoisted_7 = ["href"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "ag-cands" }
const _hoisted_11 = ["disabled", "onClick"]
const _hoisted_12 = { class: "ag-note" }
const _hoisted_13 = { class: "ag-actions" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  key: 3,
  class: "ag-problem"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { loadAgendor, requestAgendorLink, setAgendorLink, useAgendor } from '@/composables/useAgendor'
import { findAgendorContact, linkAgendorPerson, unlinkAgendorContact, type AgendorActionResult, type AgendorPerson } from '@/services/wootdashAgendor'


export default /*@__PURE__*/_defineComponent({
  __name: 'AgendorBadge',
  props: {
    contactId: {},
    conversationId: { default: null },
    variant: { default: 'row' },
    showUnlinked: { type: Boolean, default: false },
    label: { type: Boolean, default: false }
  },
  setup(__props: any) {

// Ícone do Agendor (plano-agendor.md, 3.1): preenchido = contato vinculado (tooltip com pessoa, empresa e
// responsável); vazado = não vinculado (só onde `showUnlinked`, ex: cabeçalho do chat). Clique abre o painel com
// Ver no Agendor, Atualizar, Desvincular ou Procurar pelo telefone.
const props = __props

const ORIGINS: Record<string, string> = { Bot: 'Cadastrado pelo bot', Agent: 'Vinculado por um agente', PhoneMatch: 'Achado pelo telefone' }

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const { access, links } = useAgendor()

const link = computed(() => (props.contactId ? links.get(props.contactId) ?? null : null))
const visible = computed(() => !!access.value?.allowed && !!props.contactId && (!!link.value || props.showUnlinked))
const title = computed(() => {
  const l = link.value
  if (!l) return 'Agendor: não vinculado — clique pra procurar'
  return ['Agendor', l.personName, l.organizationName, l.ownerName ? `responsável ${l.ownerName}` : null].filter(Boolean).join(' · ')
})

const open = ref(false)
const busy = ref(false)
const problem = ref<string | null>(null)
const notFound = ref(false)
const candidates = ref<AgendorPerson[]>([])
const btn = ref<HTMLElement | null>(null)
const pop = ref<HTMLElement | null>(null)
const popStyle = ref<Record<string, string>>({})

function ensure() {
  void loadAgendor(token.value).then(() => requestAgendorLink(props.contactId))
}
onMounted(ensure)
watch(() => props.contactId, () => {
  ensure()
  open.value = false
})
watch(() => access.value?.allowed, allowed => { if (allowed) requestAgendorLink(props.contactId) })

function place() {
  const r = btn.value?.getBoundingClientRect()
  if (!r) return
  const width = 280
  const left = Math.max(8, Math.min(window.innerWidth - width - 8, r.left))
  popStyle.value = { top: `${r.bottom + 6}px`, left: `${left}px`, width: `${width}px` }
}

function toggle() {
  open.value = !open.value
  problem.value = null
  candidates.value = []
  notFound.value = false
  if (open.value) {
    place()
    void nextTick(() => pop.value?.querySelector<HTMLElement>('button, a')?.focus())
  }
}

function onOutside(e: MouseEvent) {
  if (open.value && !pop.value?.contains(e.target as Node) && !btn.value?.contains(e.target as Node)) open.value = false
}
function onKey(e: KeyboardEvent) { if (e.key === 'Escape') open.value = false }
onMounted(() => { document.addEventListener('click', onOutside); document.addEventListener('keydown', onKey); window.addEventListener('scroll', place, true) })
onBeforeUnmount(() => { document.removeEventListener('click', onOutside); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', place, true) })

function apply(result: AgendorActionResult) {
  problem.value = result.problem ?? result.match?.problem ?? null
  const match = result.match
  if (!result.allowed || !props.contactId || !match) return
  if (match.outcome === 'Linked' || match.outcome === 'AlreadyLinked') setAgendorLink(props.contactId, match.link)
  candidates.value = match.outcome === 'Ambiguous' ? match.candidates ?? [] : []
  notFound.value = match.outcome === 'NotFound'
  if (match.outcome === 'NoPhone') problem.value = 'O contato não tem telefone.'
}

async function find() {
  if (!props.contactId) return
  busy.value = true
  try { apply(await findAgendorContact(token.value, props.contactId, props.conversationId)) } finally { busy.value = false }
}

async function choose(personId: string) {
  if (!props.contactId) return
  busy.value = true
  try { apply(await linkAgendorPerson(token.value, props.contactId, personId, props.conversationId)) } finally { busy.value = false }
}

async function unlink() {
  if (!props.contactId || !link.value) return
  const ok = await Swal.fire({
    title: 'Desvincular do Agendor?',
    text: `O contato deixa de estar ligado a ${link.value.personName}. Nada é apagado no Agendor.`,
    icon: 'warning', showCancelButton: true, confirmButtonText: 'Desvincular', cancelButtonText: 'Cancelar',
  })
  if (!ok.isConfirmed) return
  busy.value = true
  try {
    const result = await unlinkAgendorContact(token.value, props.contactId, props.conversationId)
    problem.value = result.problem
    if (result.allowed && !result.problem) setAgendorLink(props.contactId, null)
  } finally { busy.value = false }
}

function ago(iso: string) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (minutes < 1) return 'agora'
  if (minutes < 60) return `há ${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `há ${hours} h`
  return `há ${Math.round(hours / 24)} dia(s)`
}

return (_ctx: any,_cache: any) => {
  return (visible.value)
    ? (_openBlock(), _createElementBlock("span", {
        key: 0,
        class: _normalizeClass(["ag", [`ag--${__props.variant}`, { 'ag--on': !!link.value }]])
      }, [
        _createElementVNode("button", {
          ref_key: "btn",
          ref: btn,
          type: "button",
          class: "ag-btn",
          title: title.value,
          "aria-label": title.value,
          "aria-expanded": open.value,
          onClick: _withModifiers(toggle, ["stop"])
        }, [
          _cache[2] || (_cache[2] = _createElementVNode("svg", {
            width: "14",
            height: "14",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "2.2",
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "aria-hidden": "true"
          }, [
            _createElementVNode("rect", {
              x: "3",
              y: "7",
              width: "18",
              height: "13",
              rx: "2"
            }),
            _createElementVNode("path", { d: "M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" }),
            _createElementVNode("path", { d: "M3 13h18" })
          ], -1)),
          (__props.label && link.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "Agendor"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1),
        (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
          (open.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "pop",
                ref: pop,
                class: "ag-pop",
                style: _normalizeStyle(popStyle.value),
                role: "dialog",
                "aria-label": "Agendor",
                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("div", _hoisted_3, [
                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "ag-pop-brand" }, "Agendor", -1)),
                  _createElementVNode("button", {
                    type: "button",
                    class: "ag-x",
                    "aria-label": "Fechar",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (open.value = false))
                  }, "×")
                ]),
                (link.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("div", _hoisted_4, _toDisplayString(link.value.personName), 1),
                      _createElementVNode("dl", _hoisted_5, [
                        (link.value.organizationName)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _cache[4] || (_cache[4] = _createElementVNode("dt", null, "Empresa", -1)),
                              _createElementVNode("dd", null, _toDisplayString(link.value.organizationName), 1)
                            ], 64))
                          : _createCommentVNode("", true),
                        _cache[5] || (_cache[5] = _createElementVNode("dt", null, "Responsável", -1)),
                        _createElementVNode("dd", null, _toDisplayString(link.value.ownerName || '—'), 1),
                        _cache[6] || (_cache[6] = _createElementVNode("dt", null, "Origem", -1)),
                        _createElementVNode("dd", null, _toDisplayString(ORIGINS[link.value.origin]), 1),
                        _cache[7] || (_cache[7] = _createElementVNode("dt", null, "Atualizado", -1)),
                        _createElementVNode("dd", null, _toDisplayString(ago(link.value.syncedAt)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        (link.value.webUrl)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: "ag-act ag-act--main",
                              href: link.value.webUrl,
                              target: "_blank",
                              rel: "noopener"
                            }, "Ver no Agendor ↗", 8, _hoisted_7))
                          : _createCommentVNode("", true),
                        _createElementVNode("button", {
                          type: "button",
                          class: "ag-act",
                          disabled: busy.value,
                          onClick: find
                        }, _toDisplayString(busy.value ? 'Atualizando…' : 'Atualizar'), 9, _hoisted_8),
                        _createElementVNode("button", {
                          type: "button",
                          class: "ag-act ag-act--danger",
                          disabled: busy.value,
                          onClick: unlink
                        }, "Desvincular", 8, _hoisted_9)
                      ])
                    ], 64))
                  : (candidates.value.length)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _cache[8] || (_cache[8] = _createElementVNode("p", { class: "ag-note" }, "Mais de uma pessoa no Agendor com este telefone. Qual é este contato?", -1)),
                        _createElementVNode("ul", _hoisted_10, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(candidates.value, (c) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: c.id
                            }, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "ag-cand",
                                disabled: busy.value,
                                onClick: ($event: any) => (choose(c.id))
                              }, [
                                _createElementVNode("b", null, _toDisplayString(c.name), 1),
                                _createElementVNode("small", null, _toDisplayString([c.organizationName, c.ownerName ? `resp. ${c.ownerName}` : null].filter(Boolean).join(' · ') || `id ${c.id}`), 1)
                              ], 8, _hoisted_11)
                            ]))
                          }), 128))
                        ])
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        _createElementVNode("p", _hoisted_12, _toDisplayString(notFound.value ? 'Nenhum cadastro no Agendor com o telefone deste contato.' : 'Contato ainda não vinculado ao Agendor.'), 1),
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "ag-act ag-act--main",
                            disabled: busy.value,
                            onClick: find
                          }, _toDisplayString(busy.value ? 'Procurando…' : 'Procurar pelo telefone'), 9, _hoisted_14)
                        ])
                      ], 64)),
                (problem.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(problem.value), 1))
                  : _createCommentVNode("", true)
              ], 4))
            : _createCommentVNode("", true)
        ]))
      ], 2))
    : _createCommentVNode("", true)
}
}

})